import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchWithAuth } from './lib/api';
import {
  Container, Typography, Paper, Box, Tabs, Tab, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardContent, Grid, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Snackbar, Alert, Divider, useTheme, useMediaQuery
} from '@mui/material';
import {
  ArrowBack, Payment, Receipt, TrendingUp, Edit, Delete, Add, AddCircleOutline
} from '@mui/icons-material';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 24 }}>
      {value === index && children}
    </div>
  );
}

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [tabValue, setTabValue] = useState(0);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  // Payment Dialog
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'bank_transfer',
    reference: '',
    notes: ''
  });
  const [selectedAllocations, setSelectedAllocations] = useState([]);

  // Debit Note Dialog
  const [debitNoteDialog, setDebitNoteDialog] = useState(false);
  const [debitNoteForm, setDebitNoteForm] = useState({
    invoice_date: new Date().toISOString().split('T')[0],
    total_amount: '',
    description: '',
    invoice_number: ''
  });
  const [savingDebitNote, setSavingDebitNote] = useState(false);
  const [editingDebitNote, setEditingDebitNote] = useState(null);

  useEffect(() => {
    fetchLedger();
  }, [id]);

  const fetchLedger = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`/api/suppliers/${id}/ledger`);
      const data = await response.json();
      setLedger(data);
    } catch (error) {
      showSnackbar('Σφάλμα φόρτωσης δεδομένων', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const handleOpenPaymentDialog = () => {
    // Pre-select unpaid invoices for allocation
    const unpaidInvoices = ledger.invoices
      .filter(inv => inv.balance_due > 0)
      .sort((a, b) => new Date(a.invoice_date) - new Date(b.invoice_date)); // FIFO
    
    setSelectedAllocations(
      unpaidInvoices.map(inv => ({
        invoice_id: inv.id,
        invoice_number: inv.invoice_number,
        invoice_date: inv.invoice_date,
        total_amount: inv.total_amount,
        balance_due: inv.balance_due,
        allocated_amount: 0
      }))
    );
    setPaymentDialog(true);
  };

  const handlePaymentAmountChange = (value) => {
    setPaymentForm({ ...paymentForm, amount: value });
    
    // Auto-allocate using FIFO (First In, First Out)
    let remainingAmount = parseFloat(value) || 0;
    const updatedAllocations = selectedAllocations.map(alloc => {
      if (remainingAmount <= 0) {
        return { ...alloc, allocated_amount: 0 };
      }
      
      const toAllocate = Math.min(remainingAmount, alloc.balance_due);
      remainingAmount -= toAllocate;
      
      return { ...alloc, allocated_amount: toAllocate };
    });
    
    setSelectedAllocations(updatedAllocations);
  };

  const handleAllocationChange = (index, value) => {
    const updated = [...selectedAllocations];
    updated[index].allocated_amount = parseFloat(value) || 0;
    setSelectedAllocations(updated);
  };

  const handleSavePayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      return showSnackbar('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
    }

    const allocationsToSave = selectedAllocations
      .filter(alloc => alloc.allocated_amount > 0)
      .map(alloc => ({
        invoice_id: alloc.invoice_id,
        allocated_amount: alloc.allocated_amount
      }));

    try {
      const response = await fetchWithAuth('/api/payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_id: parseInt(id),
          ...paymentForm,
          amount: parseFloat(paymentForm.amount),
          allocations: allocationsToSave
        })
      });

      if (response.ok) {
        showSnackbar('Η πληρωμή καταχωρήθηκε επιτυχώς!', 'success');
        setPaymentDialog(false);
        setPaymentForm({
          payment_date: new Date().toISOString().split('T')[0],
          amount: '',
          payment_method: 'bank_transfer',
          reference: '',
          notes: ''
        });
        fetchLedger();
      } else {
        showSnackbar('Σφάλμα αποθήκευσης πληρωμής', 'error');
      }
    } catch (error) {
      showSnackbar('Σφάλμα αποθήκευσης πληρωμής', 'error');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την πληρωμή;')) return;
    
    try {
      const response = await fetchWithAuth(`/api/payments/${paymentId}`, { method: 'DELETE' });
      if (response.ok) {
        showSnackbar('Η πληρωμή διαγράφηκε!', 'success');
        fetchLedger();
      } else {
        showSnackbar('Σφάλμα διαγραφής πληρωμής', 'error');
      }
    } catch (error) {
      showSnackbar('Σφάλμα διαγραφής πληρωμής', 'error');
    }
  };

  const handleSaveDebitNote = async () => {
    const amount = parseFloat(debitNoteForm.total_amount);
    if (!amount || amount <= 0) {
      return showSnackbar('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
    }

    setSavingDebitNote(true);
    try {
      const response = await fetchWithAuth('/api/supplier-invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_id: parseInt(id),
          invoice_date: debitNoteForm.invoice_date,
          invoice_number: debitNoteForm.invoice_number || null,
          description: debitNoteForm.description || 'Μεταφορά χρέους',
          amount: amount,
          total_amount: amount,
          net_amount: amount,
          vat_rate: 0,
          vat_amount: 0,
          invoice_type: 'debit_note'
        })
      });

      if (response.ok) {
        showSnackbar('Το χρεωστικό σημείωμα καταχωρήθηκε επιτυχώς!', 'success');
        setDebitNoteDialog(false);
        setDebitNoteForm({
          invoice_date: new Date().toISOString().split('T')[0],
          total_amount: '',
          description: '',
          invoice_number: ''
        });
        fetchLedger();
      } else {
        const err = await response.json().catch(() => ({}));
        showSnackbar(err.error || 'Σφάλμα αποθήκευσης χρεωστικού σημειώματος', 'error');
      }
    } catch (error) {
      showSnackbar('Σφάλμα αποθήκευσης χρεωστικού σημειώματος', 'error');
    } finally {
      setSavingDebitNote(false);
    }
  };

  const handleEditDebitNote = (inv) => {
    setEditingDebitNote(inv);
    setDebitNoteForm({
      invoice_date: inv.invoice_date,
      total_amount: String(parseFloat(inv.total_amount || inv.amount)),
      description: inv.description || '',
      invoice_number: inv.invoice_number || ''
    });
    setDebitNoteDialog(true);
  };

  const handleUpdateDebitNote = async () => {
    const amount = parseFloat(debitNoteForm.total_amount);
    if (!amount || amount <= 0) {
      return showSnackbar('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
    }

    setSavingDebitNote(true);
    try {
      const response = await fetchWithAuth(`/api/supplier-invoices/${editingDebitNote.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_id: parseInt(id),
          invoice_date: debitNoteForm.invoice_date,
          invoice_number: debitNoteForm.invoice_number || null,
          description: debitNoteForm.description || 'Μεταφορά χρέους',
          amount: amount,
          total_amount: amount,
          net_amount: amount,
          vat_rate: 0,
          vat_amount: 0,
          invoice_type: 'debit_note',
          payment_status: 'paid'
        })
      });

      if (response.ok) {
        showSnackbar('Το χρεωστικό σημείωμα ενημερώθηκε επιτυχώς!', 'success');
        setDebitNoteDialog(false);
        setEditingDebitNote(null);
        setDebitNoteForm({ invoice_date: new Date().toISOString().split('T')[0], total_amount: '', description: '', invoice_number: '' });
        fetchLedger();
      } else {
        const err = await response.json().catch(() => ({}));
        showSnackbar(err.error || 'Σφάλμα ενημέρωσης χρεωστικού σημειώματος', 'error');
      }
    } catch (error) {
      showSnackbar('Σφάλμα ενημέρωσης χρεωστικού σημειώματος', 'error');
    } finally {
      setSavingDebitNote(false);
    }
  };

  const handleDeleteDebitNote = async (invoiceId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το χρεωστικό σημείωμα;')) return;

    try {
      const response = await fetchWithAuth(`/api/supplier-invoices/${invoiceId}`, { method: 'DELETE' });
      if (response.ok) {
        showSnackbar('Το χρεωστικό σημείωμα διαγράφηκε!', 'success');
        fetchLedger();
      } else {
        showSnackbar('Σφάλμα διαγραφής χρεωστικού σημειώματος', 'error');
      }
    } catch (error) {
      showSnackbar('Σφάλμα διαγραφής χρεωστικού σημειώματος', 'error');
    }
  };

  const handleCloseDebitNoteDialog = () => {
    setDebitNoteDialog(false);
    setEditingDebitNote(null);
    setDebitNoteForm({ invoice_date: new Date().toISOString().split('T')[0], total_amount: '', description: '', invoice_number: '' });
  };

  if (loading || !ledger) {
    return (
      <Container>
        <Typography>Φόρτωση...</Typography>
      </Container>
    );
  }

  const { supplier, summary, aging, invoices, payments, transactions } = ledger;

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1, md: 3 } }}>
      <Box sx={{
        mb: { xs: 2.5, md: 3.5 },
        p: { xs: 1.5, md: 2 },
        borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(59,130,246,0.04) 0%, rgba(37,99,235,0.08) 100%)',
        border: '1px solid rgba(59,130,246,0.1)',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ flexShrink: 0 }}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{ fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {supplier.name}
            </Typography>
            {supplier.vat_number && (
              <Typography variant="body2" color="text.secondary">
                ΑΦΜ: {supplier.vat_number}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}>
          <Button
            variant="outlined"
            color="error"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<AddCircleOutline />}
            onClick={() => setDebitNoteDialog(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Μεταφορά Χρέους
          </Button>
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<Payment />}
            onClick={handleOpenPaymentDialog}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Νέα Πληρωμή
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Συνολικό Υπόλοιπο
              </Typography>
              <Typography variant="h5" color={summary.totalBalance > 0 ? 'error' : 'success'}>
                {formatCurrency(summary.totalBalance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Σύνολο Τιμολογίων
              </Typography>
              <Typography variant="h5">
                {formatCurrency(summary.totalInvoiced)}
              </Typography>
              <Typography variant="caption">
                {summary.invoiceCount} τιμολόγια
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Σύνολο Πληρωμών
              </Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrency(summary.totalPaid)}
              </Typography>
              <Typography variant="caption">
                {summary.paymentCount} πληρωμές
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Εκπρόθεσμα (90+)
              </Typography>
              <Typography variant="h5" color={aging['90+'] > 0 ? 'error' : 'text.primary'}>
                {formatCurrency(aging['90+'])}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Aging Report */}
      {summary.totalBalance > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Ανάλυση Καθυστέρησης (Aging Report)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption">Τρέχοντα</Typography>
                <Typography variant="h6">{formatCurrency(aging.current)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption">0-30 μέρες</Typography>
                <Typography variant="h6" color="warning.main">{formatCurrency(aging['0-30'])}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption">31-60 μέρες</Typography>
                <Typography variant="h6" color="warning.main">{formatCurrency(aging['31-60'])}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption">61-90 μέρες</Typography>
                <Typography variant="h6" color="error.main">{formatCurrency(aging['61-90'])}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption">90+ μέρες</Typography>
                <Typography variant="h6" color="error.main">{formatCurrency(aging['90+'])}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Ιστορικό Συναλλαγών" icon={<Receipt />} iconPosition="start" />
          <Tab label="Τιμολόγια & Χρεώσεις" icon={<Receipt />} iconPosition="start" />
          <Tab label="Πληρωμές" icon={<Payment />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Transaction History */}
      <TabPanel value={tabValue} index={0}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ημερομηνία</TableCell>
                <TableCell>Τύπος</TableCell>
                <TableCell>Αναφορά</TableCell>
                <TableCell>Περιγραφή</TableCell>
                <TableCell align="right">Χρέωση</TableCell>
                <TableCell align="right">Πίστωση</TableCell>
                <TableCell align="right">Υπόλοιπο</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((tx, idx) => (
                <TableRow key={idx}>
                  <TableCell>{tx.date}</TableCell>
                  <TableCell>
                    <Chip
                      label={tx.type === 'invoice' ? 'Τιμολόγιο' : tx.type === 'debit_note' ? 'Χρεωστικό' : tx.type === 'credit_note' ? 'Πιστωτικό' : 'Πληρωμή'}
                      color={tx.type === 'invoice' ? 'default' : tx.type === 'debit_note' ? 'error' : tx.type === 'credit_note' ? 'info' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{tx.reference}</TableCell>
                  <TableCell>{tx.description}</TableCell>
                  <TableCell align="right">
                    {tx.debit > 0 && formatCurrency(tx.debit)}
                  </TableCell>
                  <TableCell align="right">
                    {tx.credit > 0 && formatCurrency(tx.credit)}
                  </TableCell>
                  <TableCell align="right">
                    {tx.balance > 0 && formatCurrency(tx.balance)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 1: Invoices & Debit Notes */}
      <TabPanel value={tabValue} index={1}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ημερομηνία</TableCell>
                <TableCell>Τύπος</TableCell>
                <TableCell>Αριθμός</TableCell>
                <TableCell>Περιγραφή</TableCell>
                <TableCell align="right">Ποσό</TableCell>
                <TableCell align="right">Πληρώθηκε</TableCell>
                <TableCell align="right">Υπόλοιπο</TableCell>
                <TableCell>Κατάσταση</TableCell>
                <TableCell align="right">Ενέργειες</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => {
                const isDebit = inv.is_debit_note || inv.invoice_type === 'debit_note';
                const isCredit = inv.is_credit_note || inv.invoice_type === 'credit_note' || inv.invoice_type?.startsWith('5');
                return (
                <TableRow key={inv.id}>
                  <TableCell>{inv.invoice_date}</TableCell>
                  <TableCell>
                    <Chip
                      label={isDebit ? 'Χρέωση' : isCredit ? 'Πιστωτικό' : 'Τιμολόγιο'}
                      color={isDebit ? 'error' : isCredit ? 'info' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{inv.invoice_number || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {inv.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(inv.total_amount)}</TableCell>
                  <TableCell align="right">
                    {isDebit ? '-' : formatCurrency(inv.paid_amount)}
                  </TableCell>
                  <TableCell align="right">
                    {isDebit ? (
                      <Typography color="text.secondary" fontWeight="bold">-</Typography>
                    ) : (
                      <Typography
                        color={inv.balance_due > 0 ? 'error' : 'success'}
                        fontWeight="bold"
                      >
                        {formatCurrency(inv.balance_due)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {isDebit ? (
                      <Chip label="Χρέωση" color="error" size="small" variant="outlined" />
                    ) : inv.balance_due === 0 ? (
                      <Chip label="Πληρωμένο" color="success" size="small" />
                    ) : inv.paid_amount > 0 ? (
                      <Chip label="Μερική" color="warning" size="small" />
                    ) : (
                      <Chip label="Ανοιχτό" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {isDebit && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditDebitNote(inv)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteDebitNote(inv.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 2: Payments */}
      <TabPanel value={tabValue} index={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ημερομηνία</TableCell>
                <TableCell>Ποσό</TableCell>
                <TableCell>Μέθοδος</TableCell>
                <TableCell>Αναφορά</TableCell>
                <TableCell>Κατανομή</TableCell>
                <TableCell align="right">Ενέργειες</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{payment.payment_date}</TableCell>
                  <TableCell>{formatCurrency(payment.amount)}</TableCell>
                  <TableCell>
                    <Chip label={payment.payment_method} size="small" />
                  </TableCell>
                  <TableCell>{payment.reference || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {payment.allocated_to || 'Χωρίς κατανομή'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeletePayment(payment.id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Νέα Πληρωμή - {supplier.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ημερομηνία Πληρωμής"
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ποσό"
                type="number"
                value={paymentForm.amount}
                onChange={(e) => handlePaymentAmountChange(e.target.value)}
                InputProps={{ startAdornment: '€' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Μέθοδος Πληρωμής</InputLabel>
                <Select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  label="Μέθοδος Πληρωμής"
                >
                  <MenuItem value="cash">Μετρητά</MenuItem>
                  <MenuItem value="bank_transfer">Τραπεζική Κατάθεση</MenuItem>
                  <MenuItem value="check">Επιταγή</MenuItem>
                  <MenuItem value="card">Κάρτα</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Αριθμός Αναφοράς"
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Σημειώσεις"
                multiline
                rows={2}
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Κατανομή σε Τιμολόγια
          </Typography>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Το ποσό κατανέμεται αυτόματα στα παλαιότερα τιμολόγια (FIFO). Μπορείτε να το τροποποιήσετε χειροκίνητα.
          </Typography>
          
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Τιμολόγιο</TableCell>
                  <TableCell>Ημερομηνία</TableCell>
                  <TableCell align="right">Υπόλοιπο</TableCell>
                  <TableCell align="right">Κατανομή</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedAllocations.map((alloc, idx) => (
                  <TableRow key={alloc.invoice_id}>
                    <TableCell>{alloc.invoice_number}</TableCell>
                    <TableCell>{alloc.invoice_date}</TableCell>
                    <TableCell align="right">{formatCurrency(alloc.balance_due)}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={alloc.allocated_amount}
                        onChange={(e) => handleAllocationChange(idx, e.target.value)}
                        inputProps={{ step: 0.01, min: 0, max: alloc.balance_due }}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Σύνολο Κατανεμημένο: {formatCurrency(selectedAllocations.reduce((sum, a) => sum + (a.allocated_amount || 0), 0))}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Υπόλοιπο Πληρωμής: {formatCurrency((parseFloat(paymentForm.amount) || 0) - selectedAllocations.reduce((sum, a) => sum + (a.allocated_amount || 0), 0))}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>Ακύρωση</Button>
          <Button variant="contained" onClick={handleSavePayment}>
            Αποθήκευση
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debit Note Dialog (Create & Edit) */}
      <Dialog open={debitNoteDialog} onClose={handleCloseDebitNoteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDebitNote ? 'Επεξεργασία Χρέωσης' : 'Μεταφορά Χρέους'} - {supplier.name}
        </DialogTitle>
        <DialogContent>
          {!editingDebitNote && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Καταχωρήστε επιπλέον χρέος προς τον προμηθευτή. Το ποσό θα προστεθεί στο συνολικό υπόλοιπο.
            </Typography>
          )}
          {!editingDebitNote && summary.totalBalance > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Τρέχον υπόλοιπο: {formatCurrency(summary.totalBalance)}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ημερομηνία"
                type="date"
                value={debitNoteForm.invoice_date}
                onChange={(e) => setDebitNoteForm({ ...debitNoteForm, invoice_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ποσό Χρέους"
                type="number"
                value={debitNoteForm.total_amount}
                onChange={(e) => setDebitNoteForm({ ...debitNoteForm, total_amount: e.target.value })}
                InputProps={{ startAdornment: '€' }}
                inputProps={{ step: 0.01, min: 0.01 }}
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Αριθμός Παραστατικού (προαιρετικό)"
                value={debitNoteForm.invoice_number}
                onChange={(e) => setDebitNoteForm({ ...debitNoteForm, invoice_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Αιτιολογία"
                multiline
                rows={2}
                value={debitNoteForm.description}
                onChange={(e) => setDebitNoteForm({ ...debitNoteForm, description: e.target.value })}
                placeholder="π.χ. Μεταφορά υπολοίπου, Χρέωση μεταφορικών..."
              />
            </Grid>
          </Grid>
          {!editingDebitNote && debitNoteForm.total_amount && parseFloat(debitNoteForm.total_amount) > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Νέο υπόλοιπο μετά την καταχώρηση: {formatCurrency((summary.totalBalance || 0) + parseFloat(debitNoteForm.total_amount))}
            </Alert>
          )}
          {editingDebitNote && debitNoteForm.total_amount && parseFloat(debitNoteForm.total_amount) > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Νέο ποσό χρέωσης: {formatCurrency(parseFloat(debitNoteForm.total_amount))}
              {parseFloat(debitNoteForm.total_amount) !== parseFloat(editingDebitNote.total_amount || editingDebitNote.amount) && (
                <> (πριν: {formatCurrency(editingDebitNote.total_amount || editingDebitNote.amount)})</>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDebitNoteDialog}>Ακύρωση</Button>
          <Button
            variant="contained"
            color="error"
            onClick={editingDebitNote ? handleUpdateDebitNote : handleSaveDebitNote}
            disabled={savingDebitNote}
          >
            {savingDebitNote ? 'Αποθήκευση...' : editingDebitNote ? 'Ενημέρωση' : 'Καταχώρηση Χρέους'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
